import { useCallback, useEffect, useRef, useState } from "react";
import { inventoryApi, type ItemSerial } from "../../../api/inventory";

const PAGE_SIZE = 25;

interface SerialSelectModalProps {
  itemId: number;
  itemName: string;
  warehouseId: number;
  // Serials already in the basket for this item — shown pre-checked so they can be unticked
  initialSelected?: ItemSerial[];
  onClose: () => void;
  onConfirm: (serials: ItemSerial[]) => void;
}

export default function SerialSelectModal({
  itemId,
  itemName,
  warehouseId,
  initialSelected = [],
  onClose,
  onConfirm,
}: SerialSelectModalProps) {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<ItemSerial[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<number, ItemSerial>>(
    () => new Map(initialSelected.map((s) => [s.serialId, s])),
  );
  const isEditing = initialSelected.length > 0;

  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Guards against out-of-order responses when the search changes mid-request
  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);
  const nextStartRef = useRef(0);
  const hasMoreRef = useRef(true);


  const loadPage = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current && !reset) return;
      if (!reset && !hasMoreRef.current) return;
      const requestId = ++requestIdRef.current;
      const start = reset ? 0 : nextStartRef.current;
      loadingRef.current = true;
      setIsLoading(true);
      setError(null);
      try {
        const { rows: page, total: pageTotal } = await inventoryApi.getItemSerials({
          itemId,
          warehouseId,
          start,
          length: PAGE_SIZE,
          search,
        });
        if (requestId !== requestIdRef.current) return;
        nextStartRef.current = start + page.length;
        hasMoreRef.current = page.length === PAGE_SIZE && nextStartRef.current < pageTotal;
        setTotal(pageTotal);
        setRows((prev) => (reset ? page : [...prev, ...page]));
      } catch (err: any) {
        if (requestId !== requestIdRef.current) return;
        hasMoreRef.current = false;
        setError(err?.message || "Failed to load serials.");
      } finally {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [itemId, warehouseId, search],
  );

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    hasMoreRef.current = true;
    nextStartRef.current = 0;
    listRef.current?.scrollTo({ top: 0 });
    loadPage(true);
  }, [loadPage]);

  // Infinite scroll: fetch the next page when the sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadPage(false);
      },
      { root: listRef.current, rootMargin: "120px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadPage]);

  // The observer only fires on visibility changes — if a loaded page doesn't fill
  // the list (e.g. a tall modal), keep fetching while the sentinel is in view
  useEffect(() => {
    if (isLoading || !hasMoreRef.current) return;
    const list = listRef.current;
    const sentinel = sentinelRef.current;
    if (!list || !sentinel) return;
    if (sentinel.getBoundingClientRect().top <= list.getBoundingClientRect().bottom + 120) {
      loadPage(false);
    }
  }, [rows, isLoading, loadPage]);

  const toggle = (serial: ItemSerial) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(serial.serialId)) next.delete(serial.serialId);
      else next.set(serial.serialId, serial);
      return next;
    });
  };

  const handleConfirm = () => {
    // When editing, confirming with nothing ticked removes the line from the basket
    if (selected.size === 0 && !isEditing) return;
    onConfirm(Array.from(selected.values()));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-barcode-box-line text-teal-600 text-xl"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">Select Serial</h2>
              <p className="text-sm text-gray-600 truncate">{itemName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search serial..."
              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
              autoFocus
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {total} serial{total !== 1 ? "s" : ""} available
            {selected.size > 0 && ` · ${selected.size} selected`}
          </p>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto p-2 min-h-[200px]">
          {rows.map((serial) => {
            const isSelected = selected.has(serial.serialId);
            return (
              <button
                key={serial.serialId}
                onClick={() => toggle(serial)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors cursor-pointer border-2 ${
                  isSelected ? "bg-teal-50 border-teal-500" : "border-transparent hover:bg-gray-50"
                }`}
              >
                <span
                  className={`w-5 h-5 flex items-center justify-center rounded border-2 flex-shrink-0 ${
                    isSelected ? "bg-teal-600 border-teal-600" : "border-gray-300"
                  }`}
                >
                  {isSelected && <i className="ri-check-line text-white text-sm"></i>}
                </span>
                <span className="flex-1 text-sm font-mono font-semibold text-gray-900 truncate">
                  {serial.serial}
                </span>
                {serial.binId != null && (
                  <span className="text-xs text-gray-500 whitespace-nowrap">Bin {serial.binId}</span>
                )}
              </button>
            );
          })}

          <div ref={sentinelRef} className="h-1" />

          {isLoading && (
            <div className="flex items-center justify-center py-4 text-gray-500 text-sm">
              <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
              Loading serials...
            </div>
          )}
          {!isLoading && error && (
            <div className="text-center py-4">
              <p className="text-sm text-red-600 mb-2">{error}</p>
              <button
                onClick={() => {
                  hasMoreRef.current = true;
                  loadPage(rows.length === 0);
                }}
                className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}
          {!isLoading && !error && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10">
              <i className="ri-inbox-line text-5xl text-gray-300 mb-2"></i>
              <p className="text-sm text-gray-600">
                {search ? "No serials match your search" : "No serials available in stock"}
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selected.size === 0 && !isEditing}
            className={`flex-1 py-2.5 text-white rounded-lg font-bold transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
              isEditing && selected.size === 0 ? "bg-red-600 hover:bg-red-700" : "bg-teal-600 hover:bg-teal-700"
            }`}
          >
            <i className={`${isEditing && selected.size === 0 ? "ri-delete-bin-line" : "ri-shopping-cart-line"} mr-2`}></i>
            {isEditing
              ? selected.size === 0
                ? "Remove from Basket"
                : `Update Basket (${selected.size})`
              : `Add ${selected.size > 0 ? `${selected.size} ` : ""}to Basket`}
          </button>
        </div>
      </div>
    </div>
  );
}
